import apiClient from './apiClient';

// ============================================================
// Types -- matching backend Go models (p2201/models/p2201.go)
// ============================================================

export interface F2110 {
  f2110_01: string;          // Receipt No (PK)
  f2110_07: string;          // Warehouse code
  f2110_11: string | null;   // Receipt date
  f2110_12: string;          // Person code
  f2110_13: string;          // Vendor code
  f2110_14: string;          // Invoice no
  f2110_15: string;          // Description
  f2110_16: number;          // Detail count
  f2110_17: number;          // Detail sum
  f2110_18: number;          // Tax type 1=incl 5% 2=exempt
  f2110_19: number;          // Tax amount
  f2110_20: number;          // Grand total incl tax
  f2110_21: number;          // Paid amount
  f2110_23: number;          // Transportation fee
  f2110_24: number;          // Reserved
  f2110_25: number;          // Freight
  f2110_26: number;          // Reserved
  f2110_28: number;          // Subtotal excl tax
  f2110_30: string;          // Memo
  f2110_31: string;          // Number type
  f2110_80: string;          // Approval status N/P
  f2110_81: string;          // Approved by
  f2110_82: string | null;   // Approved date
  f2110_91: string;          // Modified by
  f2110_92: string | null;   // Modified at
  f2110_93: string;          // Created by

  details?: F2111[];
}

export interface F2111 {
  f2111_01: string;          // Receipt No (FK)
  f2111_02: number;          // Sequence no
  f2111_07: string;          // Warehouse code (synced)
  f2111_11: string;          // Product code
  f2111_11a: string;         // Product name
  f2111_12: string;          // Unit
  f2111_13: number;          // Quantity
  f2111_14: number;          // Unit price
  f2111_15: number;          // Amount
  f2111_18: string;          // Remark
}

// ============================================================
// Request / Response types
// ============================================================

export interface P2201ListResponse {
  data: F2110[];
  total: number;
  page: number;
  size: number;
}

export interface P2201CreateRequest {
  master: Partial<F2110>;
  details: Partial<F2111>[];
}

export interface ValidationError {
  field: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

export const p2201Api = {
  async list(page = 1, size = 20): Promise<P2201ListResponse> {
    const res = await apiClient.get('/api/p2201', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<F2110> {
    const res = await apiClient.get(`/api/p2201/${encodeURIComponent(id)}`);
    return res.data;
  },

  async copyTemplate(id: string): Promise<F2110> {
    const res = await apiClient.get(`/api/p2201/${encodeURIComponent(id)}/copy-template`);
    return res.data;
  },

  async create(data: P2201CreateRequest): Promise<F2110> {
    const res = await apiClient.post('/api/p2201', data);
    return res.data;
  },

  async update(id: string, data: P2201CreateRequest): Promise<F2110> {
    const res = await apiClient.put(`/api/p2201/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/p2201/${encodeURIComponent(id)}`);
  },

  /** Shared lookup: employee, vendor/supplier, warehouse, product */
  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(`/api/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`);
      return res.data.name || '';
    } catch {
      return '';
    }
  },

  /** Get product info (name + unit + price) for detail row auto-fill (BR-06) */
  async getProductInfo(code: string): Promise<{ name: string; unit: string; price: number } | null> {
    if (!code || !code.trim()) return null;
    try {
      const res = await apiClient.get('/api/lookup/product-info', { params: { code: code.trim() } });
      return res.data || null;
    } catch {
      return null;
    }
  },

  // Approval workflow
  async submitForApproval(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/p2201/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/p2201/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/p2201/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/p2201/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`/api/p2201/${encodeURIComponent(id)}/approval-history`);
    return res.data.data || [];
  },
};

export interface ApprovalResponse {
  order_no: string;
  action: string;
  old_status: string;
  new_status: string;
  message: string;
}

export interface ApprovalHistory {
  id: number;
  order_no: string;
  action: string;
  actor_id: string;
  actor_name: string;
  comment: string;
  old_status: string;
  new_status: string;
  created_at: string;
}
